import type {
  ExerciseSessionSummary,
  ProgressionStrategy,
  ProgressionState,
  RepTarget,
} from './types.js';

/**
 * Estrategias de progresion por ejercicio (docs/LOAD_RECOMMENDATION_ENGINE.md §4).
 * No hay formula universal: cada ejercicio lleva la suya en `progression_state`.
 */

export interface ProgressionOutcome {
  /** Factor sobre la carga teorica (1 = mantener). */
  loadFactor: number;
  /** Objetivo de repeticiones sugerido para esta sesion. */
  repsHint: 'min' | 'center' | 'max' | 'plus_one';
  reasonCodes: string[];
}

/**
 * ¿Se completo el tope del rango SIN gastar más reserva de la prevista?
 *
 * Hasta el 23/09/2026 la condición era `rir <= objetivo`, con el signo al
 * revés: subía la carga a quien llegaba al tope apurando más de lo previsto
 * —con la reserva pedida habría hecho menos repeticiones— y no a quien llegaba
 * con reserva de sobra. Más RIR es más margen, no peor rendimiento.
 */
export function completedTopOfRange(
  session: ExerciseSessionSummary | undefined,
  target: RepTarget,
): boolean {
  if (!session || session.sets.length === 0) return false;
  return session.sets.every((set) => set.reps >= target.repsMax && set.rir >= target.rir);
}

/** ¿Todas las series quedaron con RIR igual o mayor al objetivo? (margen de sobra) */
export function metRirTarget(
  session: ExerciseSessionSummary | undefined,
  target: RepTarget,
): boolean {
  if (!session || session.sets.length === 0) return false;
  return session.sets.every((set) => set.rir >= target.rir && set.reps >= target.repsMin);
}

export function applyStrategy(
  strategy: ProgressionStrategy,
  lastSession: ExerciseSessionSummary | undefined,
  target: RepTarget,
): ProgressionOutcome {
  switch (strategy) {
    case 'double_progression': {
      if (completedTopOfRange(lastSession, target)) {
        // Sube carga y vuelve al minimo del rango.
        return { loadFactor: 1.025, repsHint: 'min', reasonCodes: ['double_progression_load_up'] };
      }
      return {
        loadFactor: 1,
        repsHint: 'plus_one',
        reasonCodes: ['double_progression_add_rep'],
      };
    }

    case 'load_progression': {
      if (metRirTarget(lastSession, target)) {
        return { loadFactor: 1.025, repsHint: 'center', reasonCodes: ['load_progression_up'] };
      }
      return { loadFactor: 1, repsHint: 'center', reasonCodes: ['load_progression_hold'] };
    }

    case 'rep_progression': {
      if (completedTopOfRange(lastSession, target)) {
        return { loadFactor: 1.05, repsHint: 'min', reasonCodes: ['rep_progression_jump'] };
      }
      return { loadFactor: 1, repsHint: 'plus_one', reasonCodes: ['rep_progression_add_rep'] };
    }

    case 'top_backoff':
      return { loadFactor: 1, repsHint: 'center', reasonCodes: ['top_backoff'] };

    case 'rir_ramp':
      return { loadFactor: 1, repsHint: 'center', reasonCodes: ['rir_ramp'] };

    default: {
      const exhaustive: never = strategy;
      throw new Error(`Estrategia no soportada: ${String(exhaustive)}`);
    }
  }
}

/** Repeticiones objetivo concretas segun la pista de la estrategia. */
export function resolveTargetReps(
  target: RepTarget,
  hint: ProgressionOutcome['repsHint'],
  lastSession: ExerciseSessionSummary | undefined,
): number {
  const center = Math.round((target.repsMin + target.repsMax) / 2);
  switch (hint) {
    case 'min':
      return target.repsMin;
    case 'max':
      return target.repsMax;
    case 'plus_one': {
      const best = lastSession?.sets.reduce((max, set) => Math.max(max, set.reps), 0) ?? 0;
      if (best === 0) return center;
      return Math.min(best + 1, target.repsMax);
    }
    case 'center':
    default:
      return center;
  }
}

/** Estrategia inicial cuando un ejercicio no tiene estado (§4.6). */
export function defaultStrategyFor(input: {
  equipmentKind: string;
  isCompound: boolean;
  smallestIncrementKg: number | null;
  currentLoadKg: number | null;
  isReadaptation: boolean;
}): ProgressionStrategy {
  if (input.isReadaptation) return 'double_progression';

  const relativeIncrement =
    input.smallestIncrementKg !== null && input.currentLoadKg !== null && input.currentLoadKg > 0
      ? input.smallestIncrementKg / input.currentLoadKg
      : 0;

  if (relativeIncrement > 0.06) return 'rep_progression';
  if (input.equipmentKind === 'machine_stack' || input.equipmentKind === 'cable') {
    return 'load_progression';
  }
  return 'double_progression';
}

export function initialProgressionState(strategy: ProgressionStrategy): ProgressionState {
  return {
    strategy,
    currentE1rmKg: null,
    consecutiveTopRange: 0,
    stallCounter: 0,
    overrideBiasPct: 0,
  };
}
